import React, { useState, useEffect } from "react";
import axios from "axios";
import { FiLink2, FiPackage, FiSearch, FiCheckCircle, FiAlertCircle, FiLoader } from "react-icons/fi";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";

function AssessmentMatchModal({ 
  show, 
  onClose, 
  selectedProducts, 
  onSuccess,
  supplierId,
  getToken 
}) {
  const [sourceProducts, setSourceProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSourceId, setSelectedSourceId] = useState("");
  const [matching, setMatching] = useState(false);
  const [error, setError] = useState("");

  const getSourceProductKey = (product) =>
    product?.product_id ?? product?.product_key;

  const getSelectedProductCode = (p) =>
    p?.code || p?.product?.code || p?.overview?.code || "N/A";

  const getSelectedProductName = (p) =>
    p?.name || p?.product?.name || p?.overview?.name || "Unknown";

  // Fetch products with completed assessments when modal opens
  useEffect(() => {
    let cancelled = false;

    const fetchSourceProducts = async () => {
      setLoading(true);
      setError("");
      try {
        const token = await getToken();
        const response = await axios.get(`${API_BASE_URL}/lca/assessments/sources`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!cancelled && response.data.success) {
          setSourceProducts(response.data.data || []);
        }
      } catch (err) {
        console.error("Error fetching source products:", err);
        if (!cancelled) setError("Failed to load products with assessments");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (show && supplierId) {
      fetchSourceProducts();
    } else {
      setSourceProducts([]);
      setSearchQuery("");
      setSelectedSourceId("");
      setError("");
    }

    return () => {
      cancelled = true;
    };
  }, [show, supplierId, getToken]);

  // Get selected product IDs for filtering
  const selectedProductIds = selectedProducts
    .map((p) => p?.id)
    .filter(Boolean);

  // Filter source products by search query and exclude selected products
  const filteredSourceProducts = sourceProducts.filter(product => {
    const productKey = getSourceProductKey(product);
    // Exclude if it matches search query
    const matchesSearch = product.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.code?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Exclude if it's one of the selected products (prevent self-linking)
    const isSelectedProduct = productKey
      ? selectedProductIds.some((id) => String(id) === String(productKey))
      : false;
    
    return matchesSearch && !isSelectedProduct;
  });

  const selectedSourceProduct = sourceProducts.find(
    (p) => String(p.assessment_id) === String(selectedSourceId)
  );
  
  // Check if any selected products are already in the source list (would be self-linking)
  const hasSelfLink = selectedSourceProduct
    ? selectedProductIds.some(
        (id) => String(id) === String(getSourceProductKey(selectedSourceProduct))
      )
    : false;

  const handleMatch = async () => {
    if (!selectedSourceId) {
      setError("Please select a source product");
      return;
    }

    // Client-side validation: prevent linking assessment to itself
    if (hasSelfLink) {
      setError("Cannot link an assessment to itself. Please select a different source product.");
      return;
    }

    setMatching(true);
    setError("");

    try {
      const token = await getToken();
      const productIds = selectedProductIds;
      
      const response = await axios.post(
        `${API_BASE_URL}/lca/assessments/match`,
        {
          source_assessment_id: selectedSourceId,
          product_ids: productIds,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        onSuccess({
          productIds,
          sourceAssessmentId: selectedSourceId,
          sourceAssessment: selectedSourceProduct || null,
        });
        onClose();
      } else {
        const firstError = response.data?.error;
        const resultErrors = (response.data?.results || [])
          .filter((r) => !r.success && r.error)
          .map((r) => r.error);
        const message = firstError
          || resultErrors[0]
          || "Failed to match assessments";
        setError(message);
      }
    } catch (err) {
      console.error("Error matching assessments:", err);
      setError(err.response?.data?.error || "Failed to match assessments");
    } finally {
      setMatching(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div
        className={`modal fade ${show ? 'show' : ''}`}
        id="assessmentMatchModal"
        tabIndex="-1"
        role="dialog"
        aria-labelledby="assessmentMatchModalTitle"
        aria-hidden={!show}
        style={{ display: show ? 'block' : 'none', zIndex: 1055 }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
          <div className="modal-content" style={{ border: 'none', boxShadow: '0 10px 40px rgba(0,0,0,0.15)' }}>
            <div className="modal-header border-0 pb-3" style={{ background: 'linear-gradient(135deg, #f7fcf9 0%, #ffffff 100%)' }}>
              <h5 className="modal-title d-flex align-items-center gap-2" id="assessmentMatchModalTitle" style={{ fontWeight: 600, color: '#0f5132' }}>
                <FiLink2 size={22} />
                Link Assessment to Products
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
                disabled={matching}
                style={{ opacity: matching ? 0.5 : 1 }}
              ></button>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              {error && (
                <div className="alert alert-danger d-flex align-items-center gap-2 mb-4" role="alert" style={{ borderRadius: '10px', border: '1px solid #dc3545' }}>
                  <FiAlertCircle size={18} />
                  <span>{error}</span>
                </div>
              )}

              {/* Step 1: Selected Products */}
              <div className="mb-4">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <div style={{ 
                    width: '28px', 
                    height: '28px', 
                    borderRadius: '50%', 
                    background: '#06a457', 
                    color: 'white', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: 600
                  }}>
                    1
                  </div>
                  <h6 className="mb-0" style={{ fontWeight: 600, color: '#1f2937' }}>
                    Products to Link ({selectedProducts.length})
                  </h6>
                </div>
                <div 
                  className="border rounded p-3" 
                  style={{ 
                    maxHeight: '180px', 
                    overflowY: 'auto',
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb !important'
                  }}
                >
                  {selectedProducts.length === 0 ? (
                    <div className="text-muted text-center py-2">No products selected</div>
                  ) : (
                    selectedProducts.map((product, idx) => (
                      <div key={idx} className="d-flex align-items-center gap-2 mb-2 pb-2" style={{ borderBottom: idx < selectedProducts.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                        <FiPackage size={16} style={{ color: '#6b7280', flexShrink: 0 }} />
                        <span className="badge" style={{ background: '#e0e7ff', color: '#3730a3', fontSize: '12px', fontWeight: 500 }}>
                          {getSelectedProductCode(product)}
                        </span>
                        <span style={{ fontSize: '14px', color: '#374151' }}>
                          {getSelectedProductName(product)}
                        </span>
                      </div>
                    ))
                  )}
                </div>
                <small className="text-muted mt-2 d-block" style={{ fontSize: '12px' }}>
                  These products will share the assessment data from the source product you select below.
                </small>
              </div>

              {/* Step 2: Source Product Selection */}
              <div className="mb-4">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <div style={{ 
                    width: '28px', 
                    height: '28px', 
                    borderRadius: '50%', 
                    background: '#06a457', 
                    color: 'white', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: 600
                  }}>
                    2
                  </div>
                  <h6 className="mb-0" style={{ fontWeight: 600, color: '#1f2937' }}>
                    Select Source Product
                  </h6>
                </div>
                
                <div className="position-relative mb-3">
                  <FiSearch 
                    size={18} 
                    style={{ 
                      position: 'absolute', 
                      left: '12px', 
                      top: '50%', 
                      transform: 'translateY(-50%)', 
                      color: '#9ca3af',
                      pointerEvents: 'none',
                      zIndex: 1
                    }} 
                  />
                  <input
                    type="text"
                    className="form-control"
                    id="sourceProductSearch"
                    placeholder="Search by product name or code..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    disabled={loading || matching}
                    style={{ 
                      paddingLeft: '40px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      fontSize: '14px'
                    }}
                  />
                </div>

                {loading ? (
                  <div className="d-flex align-items-center justify-content-center gap-2 py-4" style={{ 
                    background: '#f9fafb', 
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div className="spinner-border spinner-border-sm text-success" role="status" style={{ width: '18px', height: '18px' }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <span className="text-muted" style={{ fontSize: '14px' }}>Loading available products...</span>
                  </div>
                ) : filteredSourceProducts.length === 0 ? (
                  <div className="alert alert-warning d-flex align-items-center gap-2 mb-0" style={{ borderRadius: '8px', fontSize: '14px' }}>
                    <FiAlertCircle size={18} />
                    <span>No products with completed assessments found. {searchQuery && 'Try a different search term.'}</span>
                  </div>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <select
                      className="form-select"
                      value={selectedSourceId}
                      onChange={(e) => setSelectedSourceId(e.target.value)}
                      disabled={matching}
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #d1d5db',
                        fontSize: '14px',
                        padding: '10px 12px',
                        minHeight: '200px',
                        background: '#ffffff'
                      }}
                      size={Math.min(Math.max(filteredSourceProducts.length, 3), 8)}
                    >
                      <option value="" disabled style={{ color: '#9ca3af', fontWeight: 500 }}>
                        {filteredSourceProducts.length > 0 
                          ? `-- Choose a source product (${filteredSourceProducts.length} available) --`
                          : '-- No products available --'}
                      </option>
                      {filteredSourceProducts.map((product) => (
                        <option key={product.assessment_id} value={product.assessment_id} style={{ padding: '8px' }}>
                          {product.code ? `${product.code} - ` : ''}{product.name}
                          {product.total_emissions_kg && (() => { const n = Number(product.total_emissions_kg); return ` • ${n.toFixed(n > 0 && n < 1 ? 3 : 2)} kg CO₂e`; })()}
                        </option>
                      ))}
                    </select>
                    {filteredSourceProducts.length > 0 && (
                      <small className="text-muted mt-2 d-block" style={{ fontSize: '12px' }}>
                        Showing {filteredSourceProducts.length} product{filteredSourceProducts.length !== 1 ? 's' : ''} with completed assessments
                      </small>
                    )}
                  </div>
                )}
              </div>

              {/* Selected Source Preview */}
              {selectedSourceProduct && !hasSelfLink && (
                <div className="alert d-flex align-items-start gap-3 mb-0" style={{ 
                  background: '#f0f9ff', 
                  border: '1px solid #bae6fd', 
                  borderRadius: '10px',
                  padding: '16px'
                }}>
                  <FiCheckCircle size={20} style={{ color: '#0284c7', flexShrink: 0, marginTop: '2px' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: '#0c4a6e', marginBottom: '6px', fontSize: '14px' }}>
                      Source Product Selected
                    </div>
                    <div style={{ fontSize: '14px', color: '#075985', marginBottom: '4px' }}>
                      <strong>{selectedSourceProduct.code ? `${selectedSourceProduct.code} - ` : ''}{selectedSourceProduct.name}</strong>
                    </div>
                    {selectedSourceProduct.total_emissions_kg && (
                      <div style={{ fontSize: '13px', color: '#0369a1' }}>
                        Emissions: {(() => { const n = Number(selectedSourceProduct.total_emissions_kg); return `${n.toFixed(n > 0 && n < 1 ? 3 : 2)} kg CO₂e`; })()}
                      </div>
                    )}
                    <div className="mt-2" style={{ fontSize: '13px', color: '#0369a1', lineHeight: 1.5 }}>
                      ✓ All {selectedProducts.length} selected product{selectedProducts.length !== 1 ? 's' : ''} will share this assessment data
                    </div>
                  </div>
                </div>
              )}

              {selectedSourceProduct && hasSelfLink && (
                <div className="alert alert-warning d-flex align-items-start gap-3 mb-0" style={{ borderRadius: '10px' }}>
                  <FiAlertCircle size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: '14px' }}>
                      Invalid Selection
                    </div>
                    <div style={{ fontSize: '13px', lineHeight: 1.5 }}>
                      This source product is in your selected products list. You cannot link an assessment to itself. Please choose a different source product.
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer border-0 pt-0" style={{ padding: '20px 24px', background: '#f9fafb' }}>
              <button
                type="button"
                className="btn"
                onClick={onClose}
                disabled={matching}
                style={{
                  background: '#ffffff',
                  border: '1px solid #d1d5db',
                  color: '#374151',
                  borderRadius: '8px',
                  padding: '8px 20px',
                  fontWeight: 500,
                  opacity: matching ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn"
                onClick={handleMatch}
                disabled={!selectedSourceId || matching || loading || hasSelfLink}
                style={{
                  background: (!selectedSourceId || matching || loading || hasSelfLink) ? '#9ca3af' : '#06a457',
                  border: 'none',
                  color: 'white',
                  borderRadius: '8px',
                  padding: '8px 24px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s'
                }}
              >
                {matching ? (
                  <>
                    <FiLoader className="spinner" size={16} style={{ animation: 'spin 0.8s linear infinite' }} />
                    <span>Linking...</span>
                  </>
                ) : (
                  <>
                    <FiLink2 size={16} />
                    <span>Link Assessment</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      {show && (
        <div 
          className="modal-backdrop fade show" 
          onClick={matching ? undefined : onClose}
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1050 }}
        ></div>
      )}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .spinner {
          animation: spin 0.8s linear infinite;
        }
        .form-select:focus {
          border-color: #06a457;
          box-shadow: 0 0 0 0.2rem rgba(6, 164, 87, 0.25);
        }
        .form-control:focus {
          border-color: #06a457;
          box-shadow: 0 0 0 0.2rem rgba(6, 164, 87, 0.25);
        }
      `}</style>
    </>
  );
}

export default AssessmentMatchModal;

